import dayjs from 'dayjs'

const ALLOWED_SORT_FIELDS = new Set([
  'userId',
  'firstName',
  'lastName',
  'email',
  'lastLoginTimestamp',
  'households'
])

const DEFAULT_SORT = [{ field: 'lastLoginTimestamp', dir: 'desc' }]

export function isGridSortDescending (desc) {
  return desc === 'desc' || desc === true
}

/** Normalize TanStack / grid sort entries to internal 'desc' | 'asc' strings. */
export function normalizeGridSortState (sortState) {
  return (sortState ?? []).map(({ id, desc }) => ({
    id,
    desc: isGridSortDescending(desc) ? 'desc' : 'asc'
  }))
}

/**
 * HierarchicalOpalGrid propTypes expect sortState[].desc as a string.
 * TanStack treats desc as truthy/falsy, so ascending uses '' (not 'asc').
 */
export function toGridSortState (sortState) {
  return (sortState ?? []).map(({ id, desc }) => ({
    id,
    desc: isGridSortDescending(desc) ? 'desc' : ''
  }))
}

function mapSortEntry ({ id, desc }) {
  const dir = isGridSortDescending(desc) ? 'desc' : 'asc'
  if (id === 'name') {
    return [
      { field: 'lastName', dir },
      { field: 'firstName', dir }
    ]
  }
  if (ALLOWED_SORT_FIELDS.has(id)) {
    return [{ field: id, dir }]
  }
  return []
}

export function mapSortState (sortState) {
  if (!sortState?.length) return DEFAULT_SORT
  const mapped = sortState.flatMap(mapSortEntry)
  return mapped.length ? mapped : DEFAULT_SORT
}

export const DATE_RANGE_PRESETS = {
  QTD: 'QTD',
  YTD: 'YTD',
  L12M: 'L12M',
  CUSTOM: 'CUSTOM'
}

export const EMPTY_DATE_RANGE_DRAFT = {
  preset: null,
  startDate: '',
  endDate: ''
}

export const INITIAL_FILTER_STATE = {
  neverLoggedIn: false,
  selectedHouseholdClientIds: [],
  dateRangePreset: null,
  startDate: '',
  endDate: '',
  searchText: ''
}

export function resolvePresetPickerDates (preset) {
  const today = dayjs().format('YYYY-MM-DD')
  switch (preset) {
    case DATE_RANGE_PRESETS.QTD:
      return {
        startDate: dayjs().startOf('quarter').format('YYYY-MM-DD'),
        endDate: today
      }
    case DATE_RANGE_PRESETS.YTD:
      return {
        startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: today
      }
    case DATE_RANGE_PRESETS.L12M:
      return {
        startDate: dayjs().subtract(12, 'month').format('YYYY-MM-DD'),
        endDate: today
      }
    default:
      return { startDate: '', endDate: '' }
  }
}

/**
 * Resolve draft or committed date-range state to YYYY-MM-DD picker values.
 * @param {{ preset?: string|null, startDate?: string, endDate?: string }} range
 */
export function resolveDateRangePickerValues (range) {
  const preset = range?.preset ?? null
  if (!preset) {
    return { startDate: '', endDate: '' }
  }
  if (preset === DATE_RANGE_PRESETS.CUSTOM) {
    return {
      startDate: range?.startDate?.trim() ?? '',
      endDate: range?.endDate?.trim() ?? ''
    }
  }
  return resolvePresetPickerDates(preset)
}

export function dateRangePickerValuesEqual (a, b) {
  const left = resolveDateRangePickerValues(a)
  const right = resolveDateRangePickerValues(b)
  return left.startDate === right.startDate && left.endDate === right.endDate
}

export function isCustomDateRangeValid ({ preset, startDate, endDate }) {
  if (preset !== DATE_RANGE_PRESETS.CUSTOM) {
    return true
  }

  const start = startDate?.trim() ?? ''
  const end = endDate?.trim() ?? ''

  return Boolean(start && end)
}

/**
 * Top-level startDate/endDate for the login activity query (not inside filters).
 * @param {object} filterState — startDate/endDate are YYYY-MM-DD picker values
 */
export function applyDateRangeToQuery (query, filterState) {
  const next = { ...query }
  const { startDate, endDate } = resolveDateRangePickerValues({
    preset: filterState.dateRangePreset,
    startDate: filterState.startDate,
    endDate: filterState.endDate
  })
  if (startDate) {
    next.startDate = dayjs(startDate).startOf('day').toISOString()
  }
  if (endDate) {
    next.endDate = dayjs(endDate).endOf('day').toISOString()
  }
  return next
}

const compareClientId = (a, b) => String(a).localeCompare(String(b))

const normalizeClientId = (id) => String(id)

/**
 * Effective clientId payload for the login-activity query.
 * null = none selected (omit filters.clientId, show all households); non-empty = IN those ids.
 */
export function resolveHouseholdClientIdFilter (selectedHouseholdClientIds) {
  const selected = selectedHouseholdClientIds || []
  if (selected.length === 0) {
    return null
  }
  return selected
}

export function householdClientIdFiltersEqual (selectedA, selectedB) {
  const sortedA = [...(selectedA || [])].map(normalizeClientId).sort(compareClientId)
  const sortedB = [...(selectedB || [])].map(normalizeClientId).sort(compareClientId)
  if (sortedA.length !== sortedB.length) {
    return false
  }
  return sortedA.every((id, i) => id === sortedB[i])
}

/**
 * @param {object} filterState
 */
export function buildLoginActivityFilters (filterState) {
  const filters = {}
  if (filterState.neverLoggedIn) {
    filters.neverLoggedIn = true
  }

  const clientIdFilter = resolveHouseholdClientIdFilter(
    filterState.selectedHouseholdClientIds
  )
  if (clientIdFilter !== null) {
    filters.clientId = clientIdFilter
  }

  const searchText = filterState.searchText?.trim() ?? ''
  if (searchText) {
    filters.searchText = searchText
  }

  return filters
}
